import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../api';
import { User } from '@shark-games/shared';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  guestToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;

  login: (email: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string) => Promise<void>;
  loginAsGuest: () => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  refreshTokens: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      guestToken: null,
      isLoading: true,
      isAuthenticated: false,

      login: async (email, password) => {
        const response = await api.post('/auth/login', { email, password });
        const { user, accessToken, refreshToken } = response.data.data;

        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
        });
      },

      register: async (username, email, password) => {
        const response = await api.post('/auth/register', { username, email, password });
        const { user, accessToken, refreshToken } = response.data.data;

        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
        });
      },

      loginAsGuest: async () => {
        const state = get();

        // Check if we have an existing guest token
        if (state.guestToken) {
          try {
            const response = await api.post('/auth/guest/login', {
              guestToken: state.guestToken,
            });
            const { user, accessToken, refreshToken } = response.data.data;

            set({
              user,
              accessToken,
              refreshToken,
              isAuthenticated: true,
            });
            return;
          } catch {
            // Guest token invalid, create new guest
          }
        }

        // Create new guest
        const response = await api.post('/auth/guest', {});
        const { user, accessToken, refreshToken, guestToken } = response.data.data;

        set({
          user,
          accessToken,
          refreshToken,
          guestToken,
          isAuthenticated: true,
        });
      },

      logout: () => {
        const state = get();
        if (state.refreshToken) {
          api.post('/auth/logout', { refreshToken: state.refreshToken }).catch(() => {});
        }

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        });
      },

      checkAuth: async () => {
        const state = get();

        if (!state.accessToken) {
          set({ isLoading: false });
          return;
        }

        try {
          const response = await api.get('/auth/me');
          set({
            user: response.data.data.user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch {
          // Try to refresh token
          try {
            await get().refreshTokens();
            const response = await api.get('/auth/me');
            set({
              user: response.data.data.user,
              isAuthenticated: true,
              isLoading: false,
            });
          } catch {
            set({
              user: null,
              accessToken: null,
              refreshToken: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        }
      },

      refreshTokens: async () => {
        const state = get();
        if (!state.refreshToken) throw new Error('No refresh token');

        const response = await api.post('/auth/refresh', {
          refreshToken: state.refreshToken,
        });
        const { accessToken, refreshToken } = response.data.data;

        set({ accessToken, refreshToken });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        guestToken: state.guestToken,
      }),
    }
  )
);
